"use client";

import { useCallback, useRef, useState } from "react";
import { GripVertical } from "lucide-react";
import { useTranslation } from "react-i18next";
import { WelcomeAgentIcon } from "../constants/agents";
import { BetaTag } from "./beta-tag";
import { LiaNotificationTypes } from "../types";
import { cn } from "@/core/utils";
import { useRandomFacilitiesGradient } from "../hooks/useRandomFacilitiesGradient";

const DEFAULT_RIGHT = 24;
const DEFAULT_BOTTOM = 56;

function clampPosition(
  right: number,
  bottom: number,
  width: number,
  height: number,
) {
  const maxRight = Math.max(0, window.innerWidth - width);
  const maxBottom = Math.max(0, window.innerHeight - height);
  return {
    right: Math.max(0, Math.min(maxRight, right)),
    bottom: Math.max(0, Math.min(maxBottom, bottom)),
  };
}

export function LiaNotification({
  isOpen,
  onToggle,
  className,
  notificationMessage,
  hasOpenedNotification,
  onNotificationClick,
}: LiaNotificationTypes) {
  const [translate] = useTranslation(["core"]);
  const { gradientKey, style: gradientStyle } = useRandomFacilitiesGradient();
  const [position, setPosition] = useState({
    right: DEFAULT_RIGHT,
    bottom: DEFAULT_BOTTOM,
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{
    startX: number;
    startY: number;
    startRight: number;
    startBottom: number;
  } | null>(null);
  const hasDraggedRef = useRef(false);

  const handleDragStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        startRight: position.right,
        startBottom: position.bottom,
      };
    },
    [position],
  );

  const handleDragMove = useCallback((e: MouseEvent) => {
    if (!dragRef.current || !containerRef.current) return;
    hasDraggedRef.current = true;
    const { offsetWidth, offsetHeight } = containerRef.current;
    const deltaX = dragRef.current.startX - e.clientX;
    const deltaY = dragRef.current.startY - e.clientY;
    const newRight = dragRef.current.startRight + deltaX;
    const newBottom = dragRef.current.startBottom + deltaY;
    setPosition(clampPosition(newRight, newBottom, offsetWidth, offsetHeight));
  }, []);

  const handleDragEnd = useCallback(() => {
    if (dragRef.current) {
      document.removeEventListener("mousemove", handleDragMove);
      document.removeEventListener("mouseup", handleDragEnd);
      document.body.style.userSelect = "";
      dragRef.current = null;
    }
  }, [handleDragMove]);

  const handleGripMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      document.body.style.userSelect = "none";
      document.addEventListener("mousemove", handleDragMove);
      document.addEventListener("mouseup", handleDragEnd);
      handleDragStart(e);
    },
    [handleDragStart, handleDragMove, handleDragEnd],
  );

  const handleMessageClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onNotificationClick();
    },
    [onNotificationClick],
  );

  const handleMinimizedDragEnd = useCallback(() => {
    document.removeEventListener("mousemove", handleDragMove);
    document.removeEventListener("mouseup", handleMinimizedDragEnd);
    document.body.style.userSelect = "";
    dragRef.current = null;
  }, [handleDragMove]);

  const showFullNotification = !hasOpenedNotification && !isOpen;

  const handleCompactRectangleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (hasDraggedRef.current) {
        e.preventDefault();
        return;
      }
      onToggle();
    },
    [onToggle],
  );

  const handleCompactRectangleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0) return;
      hasDraggedRef.current = false;
      document.body.style.userSelect = "none";
      document.addEventListener("mousemove", handleDragMove);
      document.addEventListener("mouseup", handleMinimizedDragEnd);
      handleDragStart(e);
    },
    [handleDragStart, handleDragMove, handleMinimizedDragEnd],
  );

  const title = [
    translate("chatbot-title-facilities-prefix"),
    translate("chatbot-title-facilities-emphasis"),
    translate("chatbot-title-facilities-suffix"),
  ].join(" ");

  return (
    <div
      ref={containerRef}
      className={cn(
        "fixed z-40 flex items-center gap-0",
        "cursor-default select-none",
        className,
      )}
      style={{
        right: position.right,
        bottom: position.bottom,
      }}
    >
      {showFullNotification ? (
        /* ── Estado completo: ícone + balão de mensagem ── */
        <div className="flex items-center gap-2 bg-white rounded-2xl shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] py-2 pr-2 pl-4 min-w-[404px] max-w-[404px]">
          <button
            type="button"
            onMouseDown={handleGripMouseDown}
            className="flex items-center justify-center text-darkgray-200 cursor-grab active:cursor-grabbing touch-none"
            aria-label="Arrastar notificação"
          >
            <GripVertical className="w-5 h-5" strokeWidth={2} />
          </button>

          <img
            src={WelcomeAgentIcon}
            alt=""
            aria-hidden
            className="w-11 h-11 shrink-0"
          />

          <div
            key={gradientKey}
            role="button"
            tabIndex={0}
            onClick={handleMessageClick}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleMessageClick(e as unknown as React.MouseEvent);
              }
            }}
            style={gradientStyle}
            className="flex flex-1 items-center gap-2 px-4 py-3 animate-facilities-gradient motion-reduce:animate-none cursor-pointer hover:brightness-110 transition-[filter] min-w-0 rounded-[16px_16px_16px_0]"
            aria-label="Abrir chat do Agente de Facilities"
          >
            <p className="text-sm text-white font-medium break-words">
              {notificationMessage}
            </p>
          </div>
        </div>
      ) : (
        /* ── Estado compacto: mesmo visual do FloatingButton ── */
        <div className="flex gap-2 items-center px-2 py-3 bg-white rounded-2xl shadow-lg">
          <button
            type="button"
            onMouseDown={handleGripMouseDown}
            aria-label={translate("chatbot-aria-drag-window") as string}
            className="flex justify-center items-center -ml-1 text-darkgray-100 cursor-grab active:cursor-grabbing shrink-0"
          >
            <GripVertical className="w-5 h-5" strokeWidth={2} />
          </button>

          <img
            src={WelcomeAgentIcon}
            alt=""
            aria-hidden
            className="w-11 h-11 shrink-0"
          />

          <button
            type="button"
            onMouseDown={handleCompactRectangleMouseDown}
            onClick={handleCompactRectangleClick}
            className={cn(
              "flex items-center gap-2 h-11 px-6 rounded-full",
              "bg-agent-facilities-pill text-white font-bold text-[16px] whitespace-nowrap",
              "transition-transform duration-200 hover:scale-[1.02] active:scale-100 rounded-bl-none",
              "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-aiBlue-400",
            )}
            aria-label={
              isOpen
                ? "Fechar chat do Agente de Facilities"
                : "Abrir chat do Agente de Facilities"
            }
          >
            {title}
            <BetaTag variant="onDark" />
          </button>
        </div>
      )}
    </div>
  );
}
